'use client';

import { SuggestionChips } from './blocks/suggestion-chips';
import type { AgentSuggestion } from '@/lib/api/types';

/** Suggestions in `/agent/status` order, folded into area-labelled groups. */
function groupByArea(suggestions: AgentSuggestion[]): { area: string; prompts: string[] }[] {
  const order: string[] = [];
  const byArea = new Map<string, string[]>();

  for (const { area, prompt } of suggestions) {
    if (!byArea.has(area)) {
      byArea.set(area, []);
      order.push(area);
    }
    byArea.get(area)!.push(prompt);
  }

  return order.map((area) => ({ area, prompts: byArea.get(area) ?? [] }));
}

/**
 * What an empty conversation offers.
 *
 * The ideas come from `GET /agent/status`, which derives them from the
 * `rii/examples` of the tools this person's role can actually call — so a
 * viewer is never invited to do something they will be refused. They arrive
 * flat but each carries an `area` (e.g. "Domains", "Team"); grouping under a
 * small heading per area keeps six unrelated ideas from reading as one
 * undifferentiated wall of buttons.
 *
 * The privacy line is required (spec §5.10): messages and tool results go to
 * Anthropic, and the person typing should know before they type.
 */
export function AssistantEmptyState({
  suggestions,
  pageChips = [],
  onPick,
  disabled = false,
}: {
  suggestions: AgentSuggestion[];
  pageChips?: string[];
  onPick: (prompt: string) => void;
  disabled?: boolean;
}) {
  const groups = groupByArea(suggestions.slice(0, 6));

  return (
    <div className="flex flex-col gap-3 py-2">
      <div>
        <p className="text-sm font-medium">What would you like to set up?</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask about domains, links, routing, email forwarding or traffic. Changes are always shown
          for approval before anything happens.
        </p>
      </div>

      {pageChips.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs text-muted-foreground">About this page</p>
          <SuggestionChips options={pageChips} onPick={onPick} disabled={disabled} />
        </div>
      )}

      {groups.length > 0 && (
        <div className="flex flex-col gap-2">
          {groups.map((group) => (
            <div key={group.area}>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {group.area}
              </p>
              <SuggestionChips options={group.prompts} onPick={onPick} disabled={disabled} />
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] leading-4 text-muted-foreground">
        Your messages and the workspace data the assistant reads are sent to Anthropic to generate
        replies.
      </p>
    </div>
  );
}
